/** Rolled & delayed disruption: share of active bookings tagged rolled/delayed. */
import type { DisruptionBreakdownEntry, DisruptionPayload } from "@/types/feed";

import {
  carrierBookingsHref,
  customerBookingsHref,
  laneBookingsHref,
} from "../../feedLinks";
import { compareScale } from "../formatInsight";
import { explainerFor } from "../insightCopy";
import {
  ChangeRow,
  EntitySection,
  InsightShell,
  MetricGrid,
  type MetricItem,
} from "../primitives";

function breakdownPrimary(e: DisruptionBreakdownEntry): string {
  const parts: string[] = [];
  if (e.rolled > 0) parts.push(`${e.rolled} rolled`);
  if (e.delayed > 0) parts.push(`${e.delayed} delayed`);
  return parts.join(" \u00b7 ") || "0";
}

function BreakdownSection({
  title,
  entries,
  hrefFor,
  party = false,
  lane = false,
}: {
  title: string;
  entries: DisruptionBreakdownEntry[];
  hrefFor?: (label: string) => string | null;
  party?: boolean;
  lane?: boolean;
}) {
  const max = compareScale(entries.map((e) => e.rolled + e.delayed));
  return (
    <EntitySection title={title}>
      {entries.map((e) => (
        <ChangeRow
          key={e.label}
          name={e.label}
          href={hrefFor ? hrefFor(e.label) : null}
          party={party}
          lane={lane}
          primary={breakdownPrimary(e)}
          bar={{ type: "share", value: e.rolled + e.delayed, max }}
        />
      ))}
    </EntitySection>
  );
}

export function DisruptionView({ payload }: { payload: DisruptionPayload }) {
  const m = payload.metrics;
  const metricItems: MetricItem[] = m
    ? [
        { label: "Rolled share", value: `${Math.round(m.rolled_rate_pct)}%` },
        { label: "Delayed share", value: `${Math.round(m.delayed_rate_pct)}%` },
        { label: "Rolled", value: String(m.rolled_count) },
        { label: "Delayed", value: String(m.delayed_count) },
      ]
    : [];

  return (
    <InsightShell explainer={explainerFor(payload as unknown as Record<string, unknown>)}>
      {metricItems.length > 0 && <MetricGrid items={metricItems} />}
      {payload.top_carriers && payload.top_carriers.length > 0 && (
        <BreakdownSection
          title="Top carriers"
          entries={payload.top_carriers}
          hrefFor={carrierBookingsHref}
        />
      )}
      {payload.top_customers && payload.top_customers.length > 0 && (
        <BreakdownSection
          title="Top customers"
          entries={payload.top_customers}
          hrefFor={customerBookingsHref}
          party
        />
      )}
      {payload.top_lanes && payload.top_lanes.length > 0 && (
        <BreakdownSection
          title="Top lanes"
          entries={payload.top_lanes}
          hrefFor={laneBookingsHref}
          lane
        />
      )}
    </InsightShell>
  );
}
